import React, { useMemo } from "react";
import { Tooltip } from "antd";

export interface FlowData {
  id: string;
  title: string;
  received: number;
  approved: number;
  denied: number;
  canceled: number;
}

interface ApplicationsByFlowChartProps {
  data: FlowData[];
  onDrilldown: (flowId: string, title: string) => void;
}

export const FlowChartLegend: React.FC = () => (
  <div className="chart-legend-compact">
    <span className="legend-item">
      <span
        className="legend-color"
        style={{ backgroundColor: "#8dc756" }}
      ></span>{" "}
      Approved
    </span>
    <span className="legend-item">
      <span
        className="legend-color"
        style={{ backgroundColor: "#ff4d4f" }}
      ></span>{" "}
      Denied
    </span>
    <span className="legend-item">
      <span
        className="legend-color"
        style={{ backgroundColor: "#bac9c7" }}
      ></span>{" "}
      Canceled
    </span>
  </div>
);

export const ApplicationsByFlowChart: React.FC<
  ApplicationsByFlowChartProps
> = ({ data, onDrilldown }) => {
  // Sort: received count desc, then alpha
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      // Sort by received count descending
      if (a.received !== b.received) {
        return b.received - a.received;
      }

      // Tie-break by alphabetical order
      return a.title.toLowerCase().localeCompare(b.title.toLowerCase());
    });
  }, [data]);

  // Calculate total received for percentages
  const totalReceived = useMemo(() => {
    return sortedData.reduce((sum, d) => sum + d.received, 0);
  }, [sortedData]);

  return (
    <div className="flow-chart" style={{ overflowY: "auto" }}>
      {/* Flow items with stacked bars */}
      <div style={{ maxHeight: "280px", overflowY: "auto" }}>
        {sortedData.map((item, idx) => {
          const percentage =
            totalReceived > 0
              ? ((item.received / totalReceived) * 100).toFixed(1)
              : "0.0";

          // Calculate segment percentages within this flow's received total
          const approvedPercent =
            item.received > 0 ? (item.approved / item.received) * 100 : 0;
          const deniedPercent =
            item.received > 0 ? (item.denied / item.received) * 100 : 0;
          const canceledPercent =
            item.received > 0 ? (item.canceled / item.received) * 100 : 0;
          const pendingPercent =
            100 - approvedPercent - deniedPercent - canceledPercent;

          return (
            <Tooltip
              key={idx}
              title={
                <div>
                  <div style={{ fontWeight: "bold", marginBottom: 4 }}>
                    {item.title}
                  </div>
                  <div>Received: {item.received}</div>
                  <div>
                    Approved: {item.approved}{" "}
                    <span style={{ fontSize: "0.85em" }}>
                      ({Math.round(approvedPercent)}%)
                    </span>
                  </div>
                  <div>
                    Denied: {item.denied}{" "}
                    <span style={{ fontSize: "0.85em" }}>
                      ({Math.round(deniedPercent)}%)
                    </span>
                  </div>
                  <div>
                    Canceled: {item.canceled}{" "}
                    <span style={{ fontSize: "0.85em" }}>
                      ({Math.round(canceledPercent)}%)
                    </span>
                  </div>
                </div>
              }
              placement="top"
            >
              <div
                className="flow-item"
                onClick={() => onDrilldown(item.id || item.title, item.title)}
              >
                <div className="flow-label">{item.title}</div>
                <div className="flow-bar-stacked">
                  {approvedPercent > 0 && (
                    <div
                      className="flow-bar-segment"
                      style={{
                        width: `${approvedPercent}%`,
                        backgroundColor: "#8dc756",
                      }}
                    />
                  )}
                  {deniedPercent > 0 && (
                    <div
                      className="flow-bar-segment"
                      style={{
                        width: `${deniedPercent}%`,
                        backgroundColor: "#ff4d4f",
                      }}
                    />
                  )}
                  {canceledPercent > 0 && (
                    <div
                      className="flow-bar-segment"
                      style={{
                        width: `${canceledPercent}%`,
                        backgroundColor: "#bac9c7",
                      }}
                    />
                  )}
                  {pendingPercent > 0 && (
                    <div
                      className="flow-bar-segment"
                      style={{
                        width: `${pendingPercent}%`,
                        backgroundColor: "#f0f0f0",
                      }}
                    />
                  )}
                </div>
                <div className="flow-value">
                  {item.received} ({percentage}%)
                </div>
              </div>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};
